import { preceptorRoleFromTrack, studentRoleFromTrack, trackFromPathSegment, type Track, ROLE_META } from '@/types/roles';

/**
 * Resolve the URL path segment (which may differ from the internal Track key
 * for legacy URLs — e.g. PA → `ap`). Falls back to the track itself if no
 * explicit pathSegment is declared in ROLE_META.
 */
function pathSegmentForTrack(track: Track, category: 'student' | 'preceptor'): string {
  const meta = ROLE_META.find(m => m.track === track && m.category === category);
  return meta?.pathSegment ?? track;
}

// Helper: extract track (np/pa/img) from a pathname. Looks at first-level segment.
// URL segments use legacy `ap` for PA — we normalize to the internal Track key.
export function extractTrackFromPathname(pathname: string): Track | undefined {
  const seg = pathname.split('/').filter(Boolean)[0];
  return trackFromPathSegment(seg);
}

export function buildStudentLoginPath(track?: Track) {
  if (!track) return '/login'; // generic student login fallback
  return `/student/${pathSegmentForTrack(track, 'student')}/login`;
}

export function buildStudentSignupPath(track?: Track) {
  if (!track) return '/student/signup';
  return `/student/${pathSegmentForTrack(track, 'student')}/signup`;
}

export function buildPreceptorLoginPath(track?: Track) {
  if (!track) return '/preceptor/login'; // generic preceptor login (all tracks)
  return `/preceptor/${pathSegmentForTrack(track, 'preceptor')}/login`;
}

export function buildPreceptorSignupPath(track?: Track) {
  if (!track) return '/preceptor/signup';
  return `/preceptor/${pathSegmentForTrack(track, 'preceptor')}/signup`;
}

export function roleMetaForStudentTrack(track?: Track) {
  if (!track) return undefined;
  return ROLE_META.find(m => m.track === track && m.category === 'student');
}

export function roleMetaForPreceptorTrack(track?: Track) {
  if (!track) return undefined;
  return ROLE_META.find(m => m.track === track && m.category === 'preceptor');
}

export function resolveStudentRole(track?: Track) {
  return track ? studentRoleFromTrack(track) : undefined;
}

export function resolvePreceptorRole(track?: Track) {
  return track ? preceptorRoleFromTrack(track) : undefined;
}
